import ReferenceCurrency from '../components/ReferenceCurrency.jsx';
import TasterOffer from '../components/TasterOffer.jsx';
import { compactPackSummary } from '../components/home/packCardCopy.js';
import AccessCodeRedeemer from '../components/AccessCodeRedeemer.jsx';
import { CIE_NOTE } from '../components/home/marketingCopy.js';
import { trackItems } from '../analytics/analytics.js';
// src/pages/DashboardPage.jsx
// Migrated from prototype/between-the-lines-v2.html lines 7436-7656.
// Modifications from verbatim per Sprint 2b-2 plan section 3.6:
//   M1. Prop signature: drops the page-setter and activePack-setter props
//   M2. Adds react-router-dom useNavigate hook
//   M3. const navigate = useNavigate() at function-body top
//   M4-M10. 7 navigation call-sites converted to navigate('/...')
//           (prototype lines 7447, 7453, 7455, 7457, 7459, 7623, 7648)
//   M11-M12. 2 setActivePack call-sites commented out with Sprint 2c+ TODO
//            (prototype lines 7450, 7623)

import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReadingPrefsContext } from './useReadingPrefs.js';
import {
    IconBookOpen, IconBookmark, IconCalendar, IconCheck, IconLayers, IconQuestion, IconSparkles
} from '../components/icons.jsx';
import { getPackType, findParentBundle } from '../utils/packHelpers.js';
import {
    getIntervalLabel, getIntervalSuffix, getIntervalDescription
} from '../config/intervalOptions.js';
import {
    getPackTheme, getPackTypography, getPackFreeCount, getPackFreeLabel,
    getPackBadge, darkenHex, positionStyles, labelSizePx, subLabelSizePx,
    fitTitleFontPx, BADGE_CLASS
} from '../utils/packBadge.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { useBookmarks } from '../storage/useUserContent.js';
import { useAllProgress } from '../storage/useProgress.js';
import { CREDENTIAL_TILES } from '../config/siteCredentials.js';
import TestimonialsCarousel from '../components/TestimonialsCarousel.jsx';
import LibraryShelves from '../components/LibraryShelves.jsx';
import { HelpFeedbackTrigger } from '../components/HelpFeedback.jsx';
import ContinueCard, { resumeHref } from '../components/ContinueCard.jsx';
import { readLastVisited, lastVisitedForPacks, readPackStats } from '../storage/lastVisited.js';
import { packCompletion } from '../storage/progressHelpers.js';

// Neutral cover palette: under Minimalist (muji) AND Light (day) the per-pack theme
// colours (burgundy/forest/...) are replaced with these neutrals so covers read neutral,
// not academia-coloured. Centralised here (mirrors the --mj-* CSS vars) so the inline
// cover styles -- which CSS cannot easily reach -- stay neutral in those modes.
const MUJI_COVER = { cover: '#EDE3D2', spine: '#D8C9B2', primary: '#5E4B33', secondary: '#6E6149', line: '#D8C9B2' };

// Testimonials-carousel palette for the burgundy dashboard: the SAME shared
// TestimonialsCarousel the homepage-style block uses, in dashboard tokens. Its
// spacing is tuned for the dashboard flow (not the homepage's tall marketing
// section). Renders nothing until Danielle enables + adds testimonials.
const TESTIMONIAL_PALETTE_BURGUNDY = {
    section: 'mb-6 py-10 md:py-12 border-t border-burgundy-accent/15',
    accentText: 'text-burgundy-accent',
    mutedText: 'text-burgundy-muted',
    bodyText: 'text-burgundy-text',
    cardBorder: 'border-burgundy-accent/30',
    cardBg: 'bg-burgundy-page/40',
    hr: 'border-burgundy-accent/20',
    avatarBorder: 'border-burgundy-accent/40',
    avatarBg: 'bg-burgundy-accent/10',
};

export const DashboardPage = ({ state }) => {
    const navigate = useNavigate();
    // colorMode drives the muji cover-colour guard below (site-wide Minimalist).
    const { colorMode } = useContext(ReadingPrefsContext);
    // WP-3: ownership comes from the live entitlement listener (active = status +
    // expiry, read-only), NOT localStorage. Empty in mock / when signed out.
    const { activePackIds, entitlements } = useEntitlements();
    // Bookmarks count now comes from Firestore (per-user), not localStorage.
    const { bookmarks: fsBookmarks } = useBookmarks();
    // Progress stats come from Firestore (per-user, cross-device), not localStorage.
    const { textsRead, quizzesTaken } = useAllProgress();
    // "Continue where you left off" -- a LOCAL pointer written by the readers. Read
    // once per mount (localStorage is synchronous and this is a convenience, not state
    // to subscribe to). lastVisitedForPacks drops a pack the user can no longer see.
    const [lastVisitedRaw] = useState(() => readLastVisited());
    const [packStats] = useState(() => readPackStats());
    const lastVisited = lastVisitedForPacks(lastVisitedRaw, state.packs);
    const continuePack = lastVisited ? state.packs.find((p) => p.id === lastVisited.packId) : null;

    // Per-pack completion for the shelf rings. Two sources, in order:
    //   1. packCompletion() -- the real thing, but only when the pack carries an item
    //      total. adaptPack sets items: 0 today, so this is normally null.
    //   2. the local per-pack cache the readers write, which is the only place the
    //      totals exist without adding a Firestore read (forbidden this phase).
    // Neither -> null -> the book draws a faint empty ring.
    const completionFor = (pack) => {
        const direct = packCompletion(null, pack);
        if (direct) return direct;
        const cached = packStats[pack.id];
        if (!cached) return null;
        return { done: cached.done, total: cached.total, percent: Math.round((cached.done / cached.total) * 100) };
    };
    const continuePercent = (() => {
        if (!continuePack) return null;
        if (lastVisited && typeof lastVisited.done === 'number' && lastVisited.total > 0) {
            return Math.round((lastVisited.done / lastVisited.total) * 100);
        }
        const c = completionFor(continuePack);
        return c ? c.percent : null;
    })();

    const isInCart = (id) => state.cart.some(i => i.id === id);
    const isPurchased = (id) => activePackIds.has(id);

    const addToCart = (pack) => {
        if (!isInCart(pack.id) && !isPurchased(pack.id)) {
            state.setCart([...state.cart, pack]);
            trackItems('add_to_cart', [pack]);
        }
    };
    const buyNow = (pack) => {
        if (!isInCart(pack.id) && !isPurchased(pack.id)) {
            state.setCart([...state.cart, pack]);
            trackItems('add_to_cart', [pack]);
        }
        navigate('/checkout');
    };
    const openPack = (pack) => {
        // Phase 1.5: per-pack selection-setter for study-app per-pack navigation.
        // Route by pack TYPE, passing the pack id so a pack opens ITS OWN reader.
        // set-text, literature-study, skills-workshop + poetry-anthology have generic readers (/<type>/:packId).
        const type = getPackType(pack);
        if (type === 'set-text') {
            navigate('/set-text/' + pack.id);
        } else if (type === 'skills-workshop') {
            navigate('/skills-workshop/' + pack.id);
        } else if (type === 'literature-study') {
            navigate('/literature-study/' + pack.id);
        } else if (type === 'poetry-anthology') {
            navigate('/poetry-anthology/' + pack.id);
        } else {
            navigate('/pack-placeholder');
        }
    };

    // Bundle clarity: scroll the shopper to the parent bundle's card (same
    // storefront). No-op if the bundle card is not currently in the DOM (e.g. a
    // different, collapsed shelf) -- the relabel text still names the bundle.
    const goToBundle = (bundleId) => {
        const el = document.getElementById('pack-card-' + bundleId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Set text pack is always accessible (first 2 texts free)
    const setTextUnlocked = true;

    // renderPackCard: the library pack card, moved VERBATIM from the inline
    // state.packs.map JSX in the grid below (library-shelf-view slice) so the
    // mobile (<sm) grid and the LibraryShelves reveal share the identical,
    // single-source card. Markup, classes and logic are byte-for-byte the
    // previous inline card (indentation kept from its original context).
    const renderPackCard = (pack, idx) => {
                        const isSetText = getPackType(pack) === 'set-text';
                        // Series/Bundle S1: a series is buy-only -- it has no reader of its
                        // own (openPack would land on the placeholder route), so the card
                        // never offers Open Pack / Preview for it. Member names resolve from
                        // the loaded catalog; unresolvable ids (unpublished members) show as ids.
                        const isSeries = getPackType(pack) === 'series';
                        const memberNames = isSeries
                            ? (pack.memberPackIds || []).map((mid) => {
                                const member = state.packs.find((p) => p.id === mid);
                                return member ? member.label : mid;
                            })
                            : [];
                        const owned = isPurchased(pack.id);
                        // Bundle clarity: a member sold ONLY inside a bundle
                        // (purchaseEnabled=false -> purchasable=false). Only relevant
                        // when NOT owned (owned members keep Open Pack -> no lockout).
                        const bundleOnly = pack.purchasable === false;
                        const parentBundle = (bundleOnly && !owned) ? findParentBundle(state.packs, pack.id) : null;
                        const freePreviewCount = getPackFreeCount(state, pack);
                        const canPreview = freePreviewCount > 0 && pack.status !== 'coming-soon' && !isSeries;
                        const previewLabel = getPackFreeLabel(pack);
                        const inCart = isInCart(pack.id);
                        const isComingSoon = pack.status === 'coming-soon';
                        const badge = getPackBadge(pack, state);
                        const theme = getPackTheme(pack);
                        const typography = getPackTypography(pack);
                        // Neutralise the inline pack-cover colours in Minimalist (muji) only.
                        // library-2c: Light (day) NO LONGER neutralises -- Light is the default
                        // theme, so neutralising there hid the whole pack palette from almost
                        // everyone. Minimalist stays deliberately colourless.
                        const neutralCover = colorMode === 'muji';
                        // theme already resolves coverColor > swatch key, and derives the spine.
                        const coverBg = neutralCover ? MUJI_COVER.cover : theme.cover;
                        const spineBg = neutralCover ? MUJI_COVER.spine : theme.spine;
                        // Use full border-left shorthand so the theme spine wins over the .book-cover CSS rule
                        // Spine driven by CSS variables so the hover rule can transition colour.
                        const coverStyle = {
                            background: coverBg,
                            '--spine-color': spineBg,
                            '--spine-hover-color': darkenHex(spineBg, 0.15)
                        };
                        const titleStyle = isComingSoon ? undefined : { color: neutralCover ? MUJI_COVER.primary : theme.primary };
                        const subStyle   = isComingSoon ? undefined : { color: neutralCover ? MUJI_COVER.secondary : theme.secondary };
                        const lineStyle  = { backgroundColor: neutralCover ? MUJI_COVER.line : theme.line };
                        // The decorative gold frame (.book-cover::after) is inset left:32 right:20
                        // (index.css), but positionStyles insets the label block at only 16-24px,
                        // so the title crossed the frame line -- most visible on narrow phone covers.
                        // Add 16px horizontal padding so the block's CONTENT clears the frame on every
                        // position variant (min inset 16 + 16 = 32 >= frame's 32/20) at all widths.
                        // box-sizing:border-box keeps the title wrapping within the padded width.
                        const labelBlockStyle = {
                            position: 'absolute',
                            ...positionStyles(typography.labelPosition),
                            paddingLeft: 16,
                            paddingRight: 16,
                            zIndex: 10
                        };
                        const labelFontPx = labelSizePx(typography.labelSize);
                        const subLabelFontPx = subLabelSizePx(typography.subLabelSize);
                        // A2: auto-fit the cover title -- shrink long titles to fit, keep
                        // the admin-chosen size as the max for short ones (see fitTitleFontPx).
                        const fittedLabelPx = fitTitleFontPx(pack.label, labelFontPx);
                        // A2-b: also make it WIDTH-responsive. fitTitleFontPx is char-count
                        // only, so a short-but-large title ("MACBETH") still overflowed a
                        // narrow phone cover. clamp(floor, <cover width>, desktop cap): the
                        // fitted px is the DESKTOP cap (reached at the widest ~290px lg cover);
                        // below that the title scales with the cover's real width (cqw;
                        // .book-cover is a CSS container) so it never exceeds the cover.
                        const coverFontSize = (px) =>
                            `clamp(${Math.min(13, px)}px, ${(px / 2.9).toFixed(2)}cqw, ${px}px)`;

                        const renderCoverLabelBlock = () => (
                            <div style={labelBlockStyle}>
                                {typography.paperCodeVisible && (
                                    // SliceM1 BUG1: on narrow phone covers the paper code (e.g. "IGCSE ENGLISH
                                    // LANGUAGE A (4EA1)") wrapped one-word-per-line + clipped, because tracking-[0.3em]
                                    // at 10px is too wide for the cover. Tighten size + tracking below sm: only;
                                    // >= sm keeps the original 10px / 0.3em (desktop byte-for-byte unchanged).
                                    <div className={`font-display text-xs sm:text-xs mb-2 font-semibold ${isComingSoon ? 'text-burgundy-muted' : ''}`}
                                        style={{ ...titleStyle }}>
                                        {pack.paper}
                                    </div>
                                )}
                                {typography.dividerVisible && (
                                    <div className={`h-px w-8 mb-3 ${typography.labelPosition === 'top-center' || typography.labelPosition === 'bottom-center' || typography.labelPosition === 'center' ? 'mx-auto' : ''}`}
                                        style={lineStyle}></div>
                                )}
                                {typography.labelVisible && (
                                    <h3 className={`book-cover-title font-display leading-tight mb-2 ${isComingSoon ? 'text-burgundy-muted/60' : ''}`}
                                        style={{ ...titleStyle, fontSize: coverFontSize(fittedLabelPx), overflowWrap: 'anywhere' }}>
                                        {pack.label.toUpperCase().split(' · ').map((part, i) => (
                                            <div key={i} style={i > 0 ? { fontSize: coverFontSize(Math.max(14, fittedLabelPx * 0.6)), marginTop: 4 } : undefined}>{part}</div>
                                        ))}
                                    </h3>
                                )}
                                {typography.dividerVisible && typography.labelVisible && (
                                    <div className={`h-px w-8 mt-3 mb-2 ${typography.labelPosition === 'top-center' || typography.labelPosition === 'bottom-center' || typography.labelPosition === 'center' ? 'mx-auto' : ''}`}
                                        style={lineStyle}></div>
                                )}
                                {typography.subLabelVisible && (
                                    <p className={`font-serif italic font-light ${isComingSoon ? 'text-burgundy-muted' : ''}`}
                                        style={{ ...subStyle, fontSize: subLabelFontPx }}>
                                        {pack.subLabel}
                                    </p>
                                )}
                            </div>
                        );

                        return (
                            <div key={pack.id} id={'pack-card-' + pack.id} className="stagger-item flex flex-col [container-type:inline-size]" style={{animationDelay: `${idx * 0.1}s`}}>
                                {/* BOOK COVER — wrapper kept non-clipping so the badge can sit outside */}
                                <div className={`relative book-cover-hover ${isComingSoon ? 'coming-soon' : ''}`}>
                                    <div className="book-cover aspect-[3/4] relative overflow-hidden" style={coverStyle}>
                                        {pack.cover && (
                                            <>
                                                <img src={pack.cover} alt={pack.label}
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                    onError={(e) => { e.target.style.display = 'none'; }} />
                                                <div className="absolute inset-0 bg-gradient-to-t from-burgundy-pageDark via-burgundy-pageDark/60 to-transparent"></div>
                                            </>
                                        )}
                                        {renderCoverLabelBlock()}
                                        {/* Shimmer strip — sweeps diagonally on hover */}
                                        <span className="book-shimmer" aria-hidden="true"></span>
                                    </div>
                                    {badge && (
                                        <div style={{ top: '-10px', right: '-10px' }}
                                            className={`absolute z-30 px-3 py-1.5 font-display text-sm shadow-md font-medium rounded-full ${BADGE_CLASS[badge.kind]}`}>
                                            {badge.label}
                                        </div>
                                    )}
                                </div>

                                {/* Cover caption/actions */}
                                <div className="mt-3 px-1">
                                    {/* Caption is OUTSIDE .book-cover, so the column wrapper carries
                                        container-type:inline-size; coverFontSize char-fits then scales the
                                        caption with the column (= cover) width, matching the cover-face
                                        title. At the widest (lg) column the cqw term hits the cap, so the
                                        desktop caption is unchanged. */}
                                    <h4 className="font-display text-burgundy-text font-semibold leading-tight mb-1"
                                        style={{ fontSize: coverFontSize(fitTitleFontPx(pack.label, 20, { refChars: 18 })), overflowWrap: 'anywhere' }}>{pack.label}</h4>
                                    <p className="font-serif text-burgundy-muted text-[14px] leading-snug mb-2 line-clamp-3">{compactPackSummary(pack)}</p>
                                    {pack.description && <details className="font-serif text-burgundy-muted text-sm mb-3">
                                        <summary className="cursor-pointer font-display text-burgundy-accent text-xs font-semibold py-2">Full contents &amp; description</summary>
                                        <p className="mt-2 leading-relaxed whitespace-pre-line break-words">{pack.description}</p>
                                    </details>}

                                    {owned && entitlements[pack.id]?.accessType === 'taster' && pack.purchasable && <button onClick={() => navigate('/pack-offer/' + pack.id + '?offer=upgrade')} className="w-full mb-2 py-2 border border-burgundy-accent/40 text-burgundy-accent text-sm">Upgrade to 12 months · apply Taster credit →</button>}
                                    {isComingSoon ? (
                                        <div className="text-center py-2 border border-burgundy-accent/20 text-burgundy-muted font-display text-[14px] font-semibold text-xs">
                                            Coming Soon
                                        </div>
                                    ) : owned && isSeries ? (
                                        // An owned series has no reader; access is via the member
                                        // packs, which appear as their own owned cards.
                                        <div className="text-center py-2 border border-burgundy-accent/30 text-burgundy-muted font-serif italic text-[13px]">
                                            Owned &middot; open the packs it includes from your library
                                        </div>
                                    ) : owned ? (
                                        <button onClick={() => openPack(pack)} className="w-full py-2 bg-burgundy-accent text-burgundy-pageDark font-display text-[14px] hover:bg-burgundy-accentLight transition-colors btn-sweep flex items-center justify-center gap-2 font-semibold text-xs">
                                            <IconBookOpen className="w-4 h-4" /> Open Pack
                                        </button>
                                    ) : bundleOnly ? (
                                        // Bundle clarity: a member sold only inside a bundle -- point at
                                        // the bundle instead of a Subscribe the server would reject
                                        // (purchaseEnabled=false). No Add to Cart -> can't buy it alone.
                                        <div className="space-y-1.5">
                                            {parentBundle ? (
                                                <button onClick={() => goToBundle(parentBundle.id)}
                                                    className="w-full py-2 border border-burgundy-accent/50 text-burgundy-accent font-display text-xs sm:text-[13px] whitespace-normal text-center leading-tight hover:bg-burgundy-accent/10 transition-colors font-semibold">
                                                    Included in the {parentBundle.label} →
                                                </button>
                                            ) : (
                                                <div className="text-center py-2 border border-burgundy-accent/20 text-burgundy-muted font-serif italic text-[13px]">
                                                    Not sold separately
                                                </div>
                                            )}
                                            {canPreview && (
                                                <button onClick={() => (state.user ? openPack(pack) : navigate('/preview/' + pack.id))} className="w-full text-center font-serif italic text-burgundy-accent text-[13px] py-1 hover:text-burgundy-accentLight transition-colors">
                                                    Preview free {previewLabel} →
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        // WP-3 simplification: 2 actions (Add to Cart + Subscribe) + trust text.
                                        // PayMe/FPS is now chosen at checkout (method selector), so the separate
                                        // card button is removed. Preview stays as a tertiary text link.
                                        <div className="space-y-1.5">
                                            {isSeries && memberNames.length > 0 && (
                                                <p className="font-serif italic text-burgundy-muted text-[12px] leading-snug mb-1">
                                                    Includes: {memberNames.join(' · ')}
                                                </p>
                                            )}
                                            <div className="flex items-baseline justify-between mb-0.5">
                                                <span className="font-display text-burgundy-muted text-[13px] font-semibold text-xs">{getIntervalLabel(pack.autoRenewInterval)}</span>
                                                {pack.price > 0 && (
                                                    <span className="font-display text-burgundy-accent text-[17px] sm:text-[22px]">HK${pack.price}{getIntervalSuffix(pack.autoRenewInterval)}</span>
                                                )}
                                            </div>
                                            <p className="font-serif italic text-burgundy-muted text-[12px] leading-snug mb-1">
                                                One payment · 12-month access · No automatic renewal
                                            </p>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {/* SliceM3: below sm:, allow the longer "ADD TO CART" to WRAP (text-center,
                                                    2 lines on the narrowest phones) instead of nowrap-spilling its border;
                                                    >= sm restores the original single-line nowrap (desktop unchanged). */}
                                                <button onClick={() => addToCart(pack)} disabled={inCart}
                                                    className={`btl-cart-outline py-2 border font-display text-xs sm:text-[14px] whitespace-normal sm:whitespace-nowrap text-center leading-tight transition-colors flex items-center justify-center gap-1 font-semibold ${inCart ? 'border-burgundy-accent/20 text-burgundy-muted cursor-not-allowed' : 'border-burgundy-accent/50 text-burgundy-accent hover:bg-burgundy-accent/10'}`}>
                                                    {inCart ? <><IconCheck className="w-4 h-4" /> In Cart</> : 'Add to Cart'}
                                                </button>
                                                <button onClick={() => buyNow(pack)} className="py-2 bg-burgundy-accent text-burgundy-pageDark font-display text-xs sm:text-[14px] whitespace-normal sm:whitespace-nowrap text-center leading-tight flex items-center justify-center hover:bg-burgundy-accentLight transition-colors btn-sweep font-semibold">
                                                    Buy Pack
                                                </button>
                                            </div>
                                            {canPreview && (
                                                // Signed OUT -> the PUBLIC read-only free preview (/preview/:packId,
                                                // getPackPreview CF) instead of bouncing off AuthGuard to /login.
                                                // Signed IN -> the real reader, unchanged.
                                                <button onClick={() => (state.user ? openPack(pack) : navigate('/preview/' + pack.id))} className="w-full text-center font-serif italic text-burgundy-accent text-[13px] py-1 hover:text-burgundy-accentLight transition-colors">
                                                    Preview free {previewLabel} →
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {!isComingSoon && !owned && !bundleOnly && pack.price > 0 && <ReferenceCurrency amount={pack.price} compact />}
                                    {!isComingSoon && !owned && !bundleOnly && pack.taster?.enabled && <TasterOffer offer={pack.taster} onChoose={() => navigate('/pack-offer/' + pack.id + '?offer=taster')} />}
                                </div>
                            </div>
                        );
    };

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-burgundy-bg paper-texture page-root-text-burgundy">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
                {/* Header */}
                <div className="mb-10">
                    <div className="flex justify-between items-center gap-3 mb-2"><div className="font-display text-burgundy-accent text-xs font-semibold">Library</div><ReferenceCurrency popover /></div>
                    <h1 className="font-display text-3xl md:text-5xl text-burgundy-text mb-2 leading-tight">
                        Welcome, {state.user?.name}
                    </h1>
                    <p className="font-serif italic text-burgundy-muted text-lg">
                        Choose a study pack to continue your work.
                    </p>
                    <div className="mt-3"><HelpFeedbackTrigger /></div>
                </div>

                {/* Stats strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
                    {[
                        { n: activePackIds.size, l: 'Packs Owned', icon: IconLayers },
                        { n: textsRead, l: 'Texts Read', icon: IconBookOpen },
                        { n: quizzesTaken, l: 'Quizzes Taken', icon: IconQuestion },
                        { n: fsBookmarks.length, l: 'Bookmarks', icon: IconBookmark }
                    ].map((s, i) => (
                        <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-burgundy-page shadow-[var(--shadow-card)] p-5">
                            <div className="font-display font-bold text-burgundy-text text-3xl mb-1">{s.n}</div>
                            <div className="font-display text-burgundy-muted text-xs font-semibold">{s.l}</div>
                        </div>
                    ))}
                </div>

                {/* Trust strip: educator credibility, from the shared credential source
                    (mirrors the homepage About tiles). A slim ribbon -- surfaces the
                    credibility without the full About section, so the storefront reads
                    premium and worth the price. */}
                <div className="mb-10 rounded-[var(--radius-card)] border border-book-line bg-burgundy-page px-4 md:px-6 py-5 text-center">
                    {state.siteText?.aboutName && (
                        <p className="font-display text-burgundy-text text-sm mb-4 font-semibold">{state.siteText.aboutName}</p>
                    )}
                    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-4 gap-4">
                        {CREDENTIAL_TILES.map((c, i) => (
                            <div key={i}>
                                <p className="font-display text-burgundy-accent text-sm font-semibold">{c.n}</p>
                                <p className="font-serif text-burgundy-muted text-xs mt-1">{c.l}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* YOUR LIBRARY */}
                <AccessCodeRedeemer />
                <div className="mb-8 flex items-center gap-4">
                    <h2 className="font-display text-burgundy-text text-xl md:text-2xl">Your Library</h2>
                    <div className="flex-grow h-px bg-book-line"></div>
                </div>

                {/* Continue where you left off -- only when there is something to resume
                    AND its pack is still visible. Resume reuses the readers' existing
                    ?item= deep link, so no reader routing changed. */}
                {continuePack && resumeHref(continuePack, lastVisited.itemId) && (
                    <ContinueCard record={lastVisited} pack={continuePack} percent={continuePercent}
                        onResume={(href) => navigate(href)} />
                )}

                {state.packsLoading && (
                    <p className="font-serif italic text-burgundy-muted text-center py-10 mb-12">
                        Loading study packs…
                    </p>
                )}
                {!state.packsLoading && state.packs.length === 0 && (
                    <p className="font-serif italic text-burgundy-muted text-center py-10 mb-12">
                        No study packs available yet.
                    </p>
                )}

                {/* Library shelves (ALL widths, phones included): additive bookshelf
                    top layer -- each exam board (pack.paper) is a shelf of leather-bound
                    books, one per pack, data-driven from state.packs; opening a shelf
                    reveals that board's cards via renderPackCard, unchanged. On phones
                    (<md) the books render as textless leather spines (CSS). */}
                <div className="mb-12">
                    {!state.packsLoading && (
                        <LibraryShelves packs={state.packs} renderCard={renderPackCard} completionFor={completionFor} />
                    )}
                </div>

                {/* Testimonials (shared component; burgundy tokens). Reads the same
                    siteText.testimonials as the homepage, so once Danielle enables + adds
                    testimonials they appear here too. Renders nothing until then. */}
                <TestimonialsCarousel
                    enabled={state.siteText?.testimonialsEnabled}
                    testimonials={state.siteText?.testimonials}
                    title={state.siteText?.testimonialsTitle}
                    subtitle={state.siteText?.testimonialsSubtitle}
                    palette={TESTIMONIAL_PALETTE_BURGUNDY} />

                <p className="font-display text-burgundy-muted text-sm mb-6">{CIE_NOTE}</p>

                {/* AI Study Partner CTA -- foregrounds the crown-jewel examiner feedback */}
                <div className="btl-library-banner rounded-[var(--radius-card)] border border-book-line bg-burgundy-deep p-6 md:p-8 mb-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                        <div>
                            <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">AI Study Partner</div>
                            <h3 className="font-display text-2xl md:text-3xl text-burgundy-text mb-2">Know what examiners actually reward</h3>
                            <p className="font-serif italic text-burgundy-muted">Ask how to move an answer up a band, what earns the marks, and how a technique works under exam pressure -- tuned to your board's marking criteria and grounded in your pack material.</p>
                        </div>
                        <button onClick={() => navigate('/study-partner')} className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-xs hover:bg-burgundy-accentLight transition-colors btn-sweep flex items-center gap-2 flex-shrink-0 font-semibold">
                            <IconSparkles className="w-4 h-4" /> Open Study Partner
                        </button>
                    </div>
                </div>

                <div className="btl-library-banner rounded-[var(--radius-card)] border border-book-line bg-burgundy-deep p-6 md:p-8 mb-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                        <div>
                            <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">Mark My Essay</div>
                            <h3 className="font-display text-2xl md:text-3xl text-burgundy-text mb-2">Turn your next draft into a stronger answer</h3>
                            <p className="font-serif text-burgundy-muted">Get AI feedback on your writing, guided by your study pack’s assessment criteria, with strengths and clear next steps.</p>
                            <p className="font-serif text-burgundy-muted text-xs mt-3">Requires an unlocked study pack with AI marking enabled, through purchase or a valid access invitation. Usage limits apply.</p>
                        </div>
                        <button onClick={() => { sessionStorage.setItem('btl_account_initial_tab', 'my-essays'); navigate('/account'); }} className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-xs hover:bg-burgundy-accentLight transition-colors btn-sweep flex items-center gap-2 flex-shrink-0 font-semibold">
                            <IconSparkles className="w-4 h-4" /> Mark My Essay
                        </button>
                    </div>
                </div>

                {/* Booking CTA */}
                <div className="btl-library-banner rounded-[var(--radius-card)] border border-book-line bg-burgundy-deep p-6 md:p-8">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                        <div>
                            <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">1-on-1 Sessions</div>
                            <h3 className="font-display text-2xl md:text-3xl text-burgundy-text mb-2">Need a closer reading?</h3>
                            <p className="font-serif italic text-burgundy-muted">Book a live session with Ms. Danielle — individual or small group.</p>
                        </div>
                        <button onClick={() => navigate('/booking')} className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-xs hover:bg-burgundy-accentLight transition-colors btn-sweep flex items-center gap-2 flex-shrink-0 font-semibold">
                            <IconCalendar className="w-4 h-4" /> Book a Session
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
